/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationService } from './migration.service';
import { MigrationJob, MigrationRunIndexEntry } from './migration.models';

/**
 * The dashboard: counts over the definitions/runs actually stored, and the
 * most recent jobs. Zeros are real — nothing is simulated; until the
 * migration service exists the running/failed counts simply stay at zero.
 */
@Component({
  selector: 'app-mig-overview',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressBarModule, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-overview.component.html'
})
export class MigrationOverviewComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly registry = inject(ConnectorRegistry);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);

  readonly loading = signal(true);
  readonly jobs = signal<MigrationJob[]>([]);
  readonly runs = signal<MigrationRunIndexEntry[]>([]);

  ngOnInit(): void {
    forkJoin({ jobs: this.service.jobs(), runs: this.service.runIndex() }).subscribe(({ jobs, runs }) => {
      this.jobs.set(jobs);
      this.runs.set(runs);
      this.loading.set(false);
    });
  }

  count(statuses: string[]): number {
    return this.jobs().filter((j) => statuses.includes(j.status)).length;
  }

  documentsMigrated(): number {
    return this.runs().reduce((sum, r) => sum + (r.statistics?.success ?? 0), 0);
  }

  /** Newest five jobs, most recent activity first. */
  recent(): MigrationJob[] {
    return [...this.jobs()].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 5);
  }

  connectorName(id: string): string {
    const connector = this.registry.get(id);
    return connector ? this.translate.instant(connector.titleKey) : (id ?? '—');
  }

  statusKey(status: string): string {
    return `DMS.MIGRATION.ST_${(status || 'draft').toUpperCase().replace(/-/g, '_')}`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'aca-mig__chip--running';
      case 'completed':
        return 'aca-mig__chip--ok';
      case 'completed-warnings':
        return 'aca-mig__chip--warn';
      case 'failed':
      case 'cancelled':
      case 'interrupted':
        return 'aca-mig__chip--fail';
      default:
        return '';
    }
  }

  dateLabel(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  duration(job: MigrationJob): string {
    if (!job.startedAt || !job.completedAt) {
      return '—';
    }
    const ms = new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime();
    if (isNaN(ms) || ms < 0) {
      return '—';
    }
    const s = Math.round(ms / 1000);
    return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
  }

  docsOf(job: MigrationJob): string {
    const s = job.statistics;
    if (s?.documents === undefined) {
      return '—';
    }
    const done = (s.success ?? 0) + (s.failed ?? 0) + (s.skipped ?? 0);
    return `${done.toLocaleString()} / ${s.documents.toLocaleString()}`;
  }

  percent(job: MigrationJob): number {
    const s = job.statistics;
    if (!s?.documents) {
      return 0;
    }
    const done = (s.success ?? 0) + (s.failed ?? 0) + (s.skipped ?? 0);
    return Math.min(100, Math.round((done / s.documents) * 100));
  }

  newMigration(): void {
    this.router.navigate(['/settings/data-migration', 'new']);
  }

  manageConnections(): void {
    this.router.navigate(['/settings/data-migration', 'connections']);
  }

  openJob(): void {
    this.router.navigate(['/settings/data-migration', 'jobs']);
  }

  trackById(_i: number, job: MigrationJob): string {
    return job.id ?? job.name;
  }
}
